import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FileText, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import useSWR from "swr";

import {
  createCauseList,
  deleteCauseList,
  editCauseList,
} from "../../api/causeApi";
import {
  AddCauseModal,
  CauseListHeader,
  CauseStats,
  CauseTable,
  DeleteCauseModal,
  causeCaseStages,
  causeCaseTypes,
  defaultCauseFormValues,
  type CauseFormData,
} from "../../components/cause-list";
import EditCauseModal from "./EditCauseModal";
import api from "../../lib/api";
import { socket } from "../../components/mediasoup/socket";
import logger from "../../utils/logger";
import { toISOForDB } from "../../utils/Date";
import { useCourtSessionOptions } from "../../hooks/useCourtSessionOptions";
import type { SearchData, SearchMode } from "../../types";
import { getCauseCourtSelectionLabels } from "../../components/cause-list/causeCourtLabels";
import {
  getCauseAdvocates,
  syncLegacyAdvocateFields,
} from "../../components/cause-list/causeAdvocateUtils";

const CAUSE_LIST_PAGE_SIZE = 10;

const CauseList: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCauses, setFilteredCauses] = useState<any[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCause, setEditCause] = useState<any>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [addSelectedLabels, setAddSelectedLabels] = useState<
    Partial<
      Record<
        | "highcourt"
        | "state"
        | "bench"
        | "benchRoom"
        | "district"
        | "complex"
        | "establishment"
        | "courtRoom",
        string
      >
    >
  >({});

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<CauseFormData>({ defaultValues: defaultCauseFormValues });

  const district = watch("district");
  const complex = watch("complex");
  const state = watch("state");
  const highcourt = watch("highcourt");
  const bench = watch("bench");
  const establishment = watch("establishment");
  const courtType = watch("courtType");
  const cnr = watch("cnr");

  const courtSearchData = toCourtSearchData({
    highcourt,
    state,
    bench,
    benchRoom: watch("benchRoom"),
    district,
    complex,
    establishment,
    courtRoom: watch("courtRoom"),
  });
  const courtSearchMode: SearchMode =
    courtType === "District Court"
      ? "district"
      : courtType === "High Court"
        ? "highCourt"
        : "cnr";
  const courtOptions = useCourtSessionOptions(courtSearchData, courtSearchMode);
  const {
    data: causeList,
    mutate,
    isLoading,
  } = useSWR(`/api/cause/get-causes`);

  useEffect(() => {
    const causes = causeList?.data || [];
    const nextFilteredCauses = causes.filter(
      (cause: any) =>
        cause.petitioner?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cause.respondent?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cause.cnr?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cause.caseNumber?.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    setFilteredCauses(nextFilteredCauses);
    setPage(1);
  }, [searchTerm, causeList?.data]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredCauses.length / CAUSE_LIST_PAGE_SIZE),
  );
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * CAUSE_LIST_PAGE_SIZE;
  const paginatedCauses = useMemo(
    () => filteredCauses.slice(startIndex, startIndex + CAUSE_LIST_PAGE_SIZE),
    [filteredCauses, startIndex],
  );

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    if (cnr?.length !== 16) return;

    const fetchCauseByCnr = async () => {
      try {
        const res = await api.post(`/api/cause/get-cause`, { cnr });

        if (res?.data?.status) {
          const cause = res.data.data;
          setAddSelectedLabels(getCauseCourtSelectionLabels(cause));
          reset({
            ...defaultCauseFormValues,
            ...cause,
            advocates: getCauseAdvocates(cause),
            courtRoom:
              cause?.courtRoom?._id || cause?.courtRoom || "",
          });
        }
      } catch (error) {
        logger.error("Failed to fetch case by CNR:", error);
      }
    };

    fetchCauseByCnr();
  }, [cnr, reset]);

  const onSubmit = async (causeData: CauseFormData) => {
    try {
      setIsSubmitting(true);

      const { hearingStatus, ...payload } = normalizeCausePayload(causeData);
      void hearingStatus;

      if (payload.courtType === "High Court") {
        const selectedBenchRoom = courtOptions.benchRooms.find(
          (room) => room.value === payload.benchRoom,
        );
        payload.courtRoomNo = selectedBenchRoom?.label || payload.courtRoomNo;
        delete (payload as any).state;
        delete (payload as any).courtRoom;
        delete (payload as any).district;
        delete (payload as any).complex;
        delete (payload as any).establishment;
      }

      if (payload.courtType === "District Court") {
        delete (payload as any).highcourt;
        delete (payload as any).bench;
        delete (payload as any).benchRoom;
        delete (payload as any).courtRoomNo;
      }

      const res = await createCauseList(payload);
      const data = res?.data;

      if (data?.status) {
        toast.success(data?.message || "Case added successfully");
        emitCauseListSocketEvent("cause-list-created", data?.data, payload);
        reset(defaultCauseFormValues);
        setAddSelectedLabels({});
        setShowForm(false);
        mutate();
      } else {
        toast.error(data?.message || "Failed to add case");
      }
    } catch (error: any) {
      toast.error(error?.message || "Error submitting form");
      logger.log("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const editCauses = async (formData: any) => {
    try {
      setIsSubmitting(true);

      const {
        createdAt,
        updatedAt,
        complexName,
        hearingStatus,
        courtName,
        __v,
        ...rest
      } = normalizeCausePayload(formData);
      void createdAt;
      void updatedAt;
      void complexName;
      void hearingStatus;
      void courtName;
      void __v;

      if (rest?.courtType === "District Court") {
        delete rest.highcourt;
        delete rest.bench;
        delete rest.benchRoom;
        delete rest.courtRoomNo;
      } else if (rest?.courtType === "High Court") {
        const selectedBenchRoom = courtOptions.benchRooms.find(
          (room) => room.value === rest.benchRoom,
        );
        rest.courtRoomNo = selectedBenchRoom?.label || rest.courtRoomNo;
        delete rest.state;
        delete rest.district;
        delete rest.complex;
        delete rest.courtRoom;
        delete rest.establishment;
      }

      const res = await editCauseList(rest);
      const data = res?.data;

      if (data?.status) {
        toast.success(data?.message || "Case updated successfully");
        emitCauseListSocketEvent("cause-list-updated", data?.data, rest);
        reset(defaultCauseFormValues);
        setShowForm(false);
        setShowEditModal(false);
        await mutate();
      } else {
        toast.error(data?.message || "Failed to updated case");
      }
    } catch (error: any) {
      toast.error(error?.message || "Error submitting form");
      logger.log("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteCause = async (id: string) => {
    setIsDeleting(true);
    const causeToDelete = (causeList?.data || []).find(
      (cause: any) => cause?._id === id,
    );

    try {
      const res = await deleteCauseList(id);
      const data = res?.data;

      if (data?.status) {
        toast.success(data?.message || "Case deleted successfully");
        emitCauseListDeleteEvent(causeToDelete);
        reset(defaultCauseFormValues);
        setShowDeleteModal(false);
        setDeleteId(null);
        mutate();
      } else {
        toast.error(data?.message || "Failed to deleted case");
      }
    } catch (error: any) {
      toast.error(error?.message);
      logger.log(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const closeAddModal = () => {
    setShowForm(false);
    setAddSelectedLabels({});
    reset(defaultCauseFormValues);
  };

  return (
    <>
      <div className="min-h-screen max-w-screen w-full">
        <div className="w-full max-w-full lg:p-4 md:p-4 p-4">
          <CauseListHeader
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            onAddCase={() => setShowForm(true)}
          />

          <CauseStats causes={filteredCauses} />

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
            </div>
          ) : filteredCauses.length > 0 ? (
            <CauseTable
              causes={paginatedCauses}
              currentPage={currentPage}
              totalPages={totalPages}
              total={filteredCauses.length}
              startIndex={startIndex}
              onPageChange={setPage}
              onEdit={(nextCause) => {
                setEditCause(nextCause);
                setShowEditModal(true);
              }}
              onDelete={(id) => {
                setDeleteId(id);
                setShowDeleteModal(true);
              }}
            />
          ) : null}
        </div>

        {showEditModal && editCause && (
          <EditCauseModal
            cause={editCause}
            onClose={() => setShowEditModal(false)}
            onSubmit={editCauses}
            caseTypes={causeCaseTypes}
            caseStages={causeCaseStages}
            isSubmitting={isSubmitting}
          />
        )}

        {!isLoading && filteredCauses?.length === 0 && (
          <div className="text-center py-16">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No cases found
            </h3>
            <p className="text-gray-600">
              Try adjusting your search or add a new case.
            </p>
          </div>
        )}
      </div>

      {showForm && (
        <AddCauseModal
          register={register}
          handleSubmit={handleSubmit}
          setValue={setValue}
          watch={watch}
          control={control}
          errors={errors}
          onClose={closeAddModal}
          onSubmit={onSubmit}
          isSubmitting={isSubmitting}
          caseTypes={causeCaseTypes}
          caseStages={causeCaseStages}
          highCourts={courtOptions.highCourts}
          states={courtOptions.states}
          benches={courtOptions.benches}
          benchRooms={courtOptions.benchRooms}
          districts={courtOptions.districts}
          complexes={courtOptions.complexes}
          establishments={courtOptions.establishments}
          courtRooms={courtOptions.courtRooms}
          courtEstablishment={courtOptions.courtEstablishment}
          courtOptionControls={courtOptions.controls}
          selectedLabels={addSelectedLabels}
        />
      )}

      {showDeleteModal && (
        <DeleteCauseModal
          isDeleting={isDeleting}
          onCancel={() => {
            setShowDeleteModal(false);
            setDeleteId(null);
          }}
          onConfirm={() => {
            if (deleteId) deleteCause(deleteId);
          }}
        />
      )}
    </>
  );
};

const normalizeCausePayload = <T extends Record<string, any>>(data: T): any => {
  const payload: any = { ...data };

  if (payload.filingDate) {
    payload.filingDate = toISOForDB(payload.filingDate) ?? "";
  }

  if (payload.registrationDate) {
    payload.registrationDate = toISOForDB(payload.registrationDate) ?? "";
  }

  if (payload.hearingDate) {
    payload.hearingDate = toISOForDB(payload.hearingDate) ?? "";
  }

  return syncLegacyAdvocateFields(payload);
};

const emitCauseListSocketEvent = (
  event: "cause-list-created" | "cause-list-updated",
  savedCause: any,
  fallbackCause: any,
) => {
  const cause = { ...(fallbackCause || {}), ...(savedCause || {}) };
  const courtRoomId = getCauseRoomId(cause);

  if (!courtRoomId) return;

  socket.emit(event, {
    courtRoomId,
    cause,
  });
};

const emitCauseListDeleteEvent = (cause: any) => {
  const courtRoomId = getCauseRoomId(cause);

  if (!courtRoomId) return;

  socket.emit("cause-list-deleted", {
    courtRoomId,
    causeId: getId(cause?._id),
    cnr: cause?.cnr,
  });
};

const getCauseRoomId = (cause: any) => {
  if (!cause) return "";
  return getId(cause.courtType === "High Court" ? cause.benchRoom : cause.courtRoom);
};

const getId = (value: any) => {
  if (!value) return "";
  if (typeof value === "object") return value._id || value.id || "";
  return value;
};

const toCourtSearchData = ({
  highcourt,
  state,
  bench,
  benchRoom,
  district,
  complex,
  establishment,
  courtRoom,
}: {
  highcourt: string;
  state: string;
  bench: string;
  benchRoom: string;
  district: string;
  complex: string;
  establishment: string;
  courtRoom: string;
}): SearchData => ({
  cnr: "",
  highCourt: "",
  state,
  highcourt,
  bench,
  benchRoom,
  district,
  courtComplex: complex,
  establishment,
  courtName: courtRoom,
  date: "",
  status: "all",
});

export default CauseList;
